'use client';

import { VoteType } from '@prisma/client';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { voteOnPhoto } from '../../../actions/reporterActions';
import { CommentData } from '../../../types/shared';
import { RealtimeProvider, useRealtime } from '../contexts/RealtimeProvider';
import { PhotoWithDetails } from '../page';
import { CommentsSection } from './CommentsSection';
import { VoteButtons } from './VoteButtons';

// --- Внутренний компонент ---
function ModalContent({
  photo,
  scrollToCommentId,
  onVote,
  onCommentAdded,
}: {
  photo: PhotoWithDetails;
  scrollToCommentId?: string | null;
  onVote?: (photoId: string, voteType: VoteType) => Promise<void> | void;
  onCommentAdded?: (comment: CommentData) => void;
}) {
  // ✅ Берем данные из контекста (там теперь актуальные цифры из SSE)
  const { likes, dislikes, myVote, optimisticVoteUpdate } = useRealtime();
  const { data: session } = useSession();

  const handleVote = useCallback(
    async (voteType: VoteType) => {
      if (!session?.user) {
        toast.error('Войдите, чтобы голосовать');
        return;
      }

      // 1. Оптимистичное обновление
      optimisticVoteUpdate(voteType); // ✅ Убрал userId, он теперь не обязателен в аргументах провайдера (если мы поправили провайдер как в прошлом ответе)

      // 2. Сервер
      try {
        if (onVote) {
          await onVote(photo.id, voteType);
        } else {
          await voteOnPhoto(photo.id, voteType);
        }
      } catch (e) {
        console.error(e);
        toast.error('Ошибка при голосовании');
      }
    },
    [photo.id, session?.user, optimisticVoteUpdate, onVote],
  );

  const handleCommentAdded = useCallback(
    (comment: CommentData) => {
      onCommentAdded?.(comment);
    },
    [onCommentAdded],
  );

  return (
    <>
      <div className="p-4 flex items-center gap-4 border-b border-slate-700 flex-shrink-0">
        <VoteButtons
          likes={likes} // ✅ Берем из Realtime
          dislikes={dislikes} // ✅ Берем из Realtime
          myVote={myVote} // ✅ Берем из Realtime
          onVote={handleVote}
        />
      </div>
      <div className="flex-grow overflow-y-auto p-4 custom-scrollbar">
        {/* custom-scrollbar - добавь в globals.css, если нет, или просто overflow-y-auto */}
        <CommentsSection
          photoId={photo.id}
          scrollToCommentId={scrollToCommentId}
          onCommentAdded={handleCommentAdded}
        />
      </div>
    </>
  );
}

// --- Обертка (Главный компонент) ---
export function PhotoViewModal({
  photo,
  onClose,
  scrollToCommentId,
  onVote,
  onCommentAdded,
}: {
  photo: PhotoWithDetails;
  onClose: () => void;
  scrollToCommentId?: string | null;
  onVote?: (photoId: string, voteType: VoteType) => Promise<void> | void;
  onCommentAdded?: (photoId: string, comment: CommentData) => void;
}) {
  const modalRef = useRef<HTMLDivElement>(null);

  const formattedDate = useMemo(
    () => format(new Date(photo.createdAt), 'd MMMM yyyy', { locale: ru }),
    [photo.createdAt],
  );

  useEffect(() => {
    modalRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleCommentAdded = useCallback(
    (comment: CommentData) => {
      onCommentAdded?.(photo.id, comment);
    },
    [onCommentAdded, photo.id],
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/70 hover:text-white z-50 p-2 rounded-full hover:bg-white/10 transition-colors"
      >
        <X size={28} />
      </button>

      <motion.div
        ref={modalRef}
        layoutId={photo.id}
        className="w-full h-full max-w-7xl max-h-[95vh] grid grid-cols-1 lg:grid-cols-3 gap-0 rounded-none sm:rounded-xl overflow-hidden shadow-2xl bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="lg:col-span-2 bg-black flex items-center justify-center relative min-h-[40vh]">
          <Image
            src={photo.imageUrl}
            alt={photo.caption || 'Фото'}
            width={1920}
            height={1080}
            className="object-contain w-full h-full"
            priority // Важно для LCP внутри модалки
          />
        </div>

        <div className="lg:col-span-1 flex flex-col h-full min-h-0 bg-slate-900">
          <div className="p-4 border-b border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <Image
                src={photo.author.image || '/default-avatar.jpg'}
                alt={photo.author.name || 'User'}
                width={40}
                height={40}
                className="rounded-full ring-2 ring-slate-700"
              />
              <div>
                <p className="font-bold text-slate-100">{photo.author.name}</p>
                <p className="text-xs text-slate-400">{formattedDate}</p>
              </div>
            </div>
            {photo.caption && (
              <div className="mt-4">
                <h2 className="text-lg font-medium text-slate-100 leading-snug">{photo.caption}</h2>
              </div>
            )}
          </div>

          {/* 🔥 ИСПРАВЛЕНИЕ: Передаем новую структуру данных в провайдер */}
          <RealtimeProvider
            photoId={photo.id}
            initialData={{
              likes: photo.likes,
              dislikes: photo.dislikes,
              myVote: photo.myVote,
              comments: photo.comments,
            }}
          >
            <ModalContent
              photo={photo}
              scrollToCommentId={scrollToCommentId}
              onVote={onVote}
              onCommentAdded={handleCommentAdded}
            />
          </RealtimeProvider>
        </div>
      </motion.div>
    </motion.div>
  );
}
